import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { IFormTheme, IThemeData } from '@xilo-mono/form-contracts';
import { catchError, type Observable, of, ReplaySubject, tap, timeout } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class FormThemeService {
  private readonly http = inject(HttpClient);
  private readonly _formTheme$ = new ReplaySubject<IFormTheme | undefined>(1);

  public readonly formTheme$ = this._formTheme$.asObservable();

  loadFormTheme(themeId: string | number | undefined, companyId: string | number | undefined, apply = true): Observable<IFormTheme | undefined> {
    if (!themeId || !companyId) {
      this._formTheme$.next(undefined);
      return of(undefined);
    }
    const apiUrl = environment.apiUrl;
    const url = new URL(
      (apiUrl.endsWith('/') ? apiUrl : (apiUrl + '/')) + 'themes/company-public/' + companyId + '/theme/' + themeId,
      window.location.origin
    );
    return this.http.get<IFormTheme>(url.toString()).pipe(
      timeout(30000),
      catchError(() => of(undefined)),
      tap((theme) => {
        this._formTheme$.next(theme);
        if (theme?.data && apply) {
          applyTheme(theme.data);
        }
      })
    );
  }
}

export function applyTheme(theme: IThemeData) {
  if (!theme) {
    return;
  }
  try {
    const root = document.documentElement;
    const fonts = theme.branding?.fonts;
    if (!fonts) {
      return;
    }
    // Apply font settings
    ['heading', 'title', 'subHeading', 'body', 'label', 'field', 'button', 'cards', 'selection'].forEach(item => {
      if (fonts[item] && typeof fonts[item] === 'object') {
        const color = fonts[item].color;
        const weight = fonts[item].weight;
        const family = fonts[item].family;
        const size = fonts[item].size;
        const position = fonts[item].position;
        if (item === 'subHeading') {
          item = 'sub-heading';
        }
        if (color) {
          root.style.setProperty(`--${item}-font-color`, color);
        }
        if (weight) {
          root.style.setProperty(`--${item}-font-weight`, weight);
        }
        if (family) {
          root.style.setProperty(`--${item}-font-family`, family);
        }
        if (size) {
          root.style.setProperty(`--${item}-font-size`, size);
        }
        if (position) {
          root.style.setProperty(`--${item}-font-position`, position);
        }
      }
    });

    // Apply additional branding settings
    if (!theme.branding) {
      return;
    }
    if (theme.branding.primaryColor) {
      root.style.setProperty('--primary-color', theme.branding.primaryColor);
    }
    if (theme.branding.primaryColor) {
      root.style.setProperty('--brand-color', theme.branding.primaryColor);
    }
    if (theme.branding.secondaryColor) {
      root.style.setProperty('--secondary-color', theme.branding.secondaryColor);
    }
    if (theme.branding.footerBannerColor) {
      root.style.setProperty('--footer-banner-color', theme.branding.footerBannerColor);
    }
    if (theme.branding.buttonColor) {
      root.style.setProperty('--button-color', theme.branding.buttonColor);
    }
    if (theme.branding.buttonBorderRadius) {
      root.style.setProperty('--button-border-radius', theme.branding.buttonBorderRadius);
    }
    if (theme.branding.formBackgroundColor) {
      root.style.setProperty('--form-background-color', theme.branding.formBackgroundColor);
    }
    if (theme.branding.navbarBackgroundColor) {
      root.style.setProperty('--navbar-background-color', theme.branding.navbarBackgroundColor);
    }
    if (theme.branding.fieldBackgroundColor) {
      root.style.setProperty('--field-background-color', theme.branding.fieldBackgroundColor);
    }
    if (theme.branding.formLogoSize) {
      root.style.setProperty('--form-logo-size', theme.branding.formLogoSize);
    }
    if (theme.branding.formIconSizes) {
      root.style.setProperty('--form-icon-sizes', theme.branding.formIconSizes);
    }
    if (theme.branding.cardBackgroundColor) {
      root.style.setProperty('--card-background-color', theme.branding.cardBackgroundColor);
    }
    if (theme.branding.cardWidth) {
      root.style.setProperty('--card-width', theme.branding.cardWidth);
    }
    if (theme.branding.cardHeight) {
      root.style.setProperty('--card-height', theme.branding.cardHeight);
    }
    if (theme.branding.cardBorderRadius != null) {
      root.style.setProperty('--card-border-radius', theme.branding.cardBorderRadius);
    }
    if (theme.branding.prevNextBtnBackgroundColor) {
      root.style.setProperty('--prev-next-btn-bg', theme.branding.prevNextBtnBackgroundColor);
    }
    if (theme.branding.prevNextBtnColor) {
      root.style.setProperty('--prev-next-btn-color', theme.branding.prevNextBtnColor);
    }
    if (theme.branding.buttonAlignPosition) {
      switch (theme.branding.buttonAlignPosition) {
        case 'center':
          root.style.setProperty('--next-button-left-position', '50%');
          root.style.setProperty('--next-button-right-position', 'unset');
          break;
        case 'right':
          root.style.setProperty('--next-button-left-position', 'unset');
          root.style.setProperty('--next-button-right-position', '10%');
          break;
        default:
          root.style.setProperty('--next-button-left-position', 'unset');
          root.style.setProperty('--next-button-right-position', 'unset');
          break;
      }
    }
  } catch (e) {
    console?.error('applyTheme:', e);
  }
}
